import React from 'react'

import {
  AlignItems,
  Box,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  FontFamily,
  Heading1,
  Overflow,
  TextAlign,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import type { CSSObject } from '@emotion/react'

import kingPrawnTeriyakiDonburi from '../../../assets/CWCFirstBoxSectionAssets/plates/king-prawn-teriyaki-donburi.png'
import kingPrawnTeriyakiDonburiSm from '../../../assets/CWCFirstBoxSectionAssets/plates/king-prawn-teriyaki-donburi-sm.png'
import kingPrawnTeriyakiDonburiWebp from '../../../assets/CWCFirstBoxSectionAssets/plates/king-prawn-teriyaki-donburi.webp'
import kingPrawnTeriyakiDonburiWebpSm from '../../../assets/CWCFirstBoxSectionAssets/plates/king-prawn-teriyaki-donburi-sm.webp'
import potatoCurry from '../../../assets/CWCFirstBoxSectionAssets/plates/potato-curry.png'
import potatoCurrySm from '../../../assets/CWCFirstBoxSectionAssets/plates/potato-curry-sm.png'
import potatoCurryWebp from '../../../assets/CWCFirstBoxSectionAssets/plates/potato-curry.webp'
import potatoCurryWebpSm from '../../../assets/CWCFirstBoxSectionAssets/plates/potato-curry-sm.webp'
import salmon from '../../../assets/CWCFirstBoxSectionAssets/plates/salmon.png'
import salmonSm from '../../../assets/CWCFirstBoxSectionAssets/plates/salmon-sm.png'
import salmonWebp from '../../../assets/CWCFirstBoxSectionAssets/plates/salmon.webp'
import salmonWebpSm from '../../../assets/CWCFirstBoxSectionAssets/plates/salmon-sm.webp'
import { DiscountBanner } from '../../AboveTheFoldSection/components/DiscountBanner/DiscountBanner'
import { Sections } from '../../AboveTheFoldSection/tracking'

export type BoxPriceCalculatorHeroProps = {
  onBannerCTAClick: () => void
}

type Responsive = [string, string | null, string, string]

// Breakpoints are [mobile, 640, tablet, desktop], read from Figma frames 6213:16865 (mobile),
// 6213:17055 (iPad) and 6487:5439 (desktop). Every plate is anchored to the centre of the page with
// calc(50% ± offset), so it stays beside the centred heading and card as the viewport widens.
// The salmon and donburi assets carry transparent padding (the dish is 78% of the image), so they
// fill 180/200px frames. The curry export is cropped to the dish, so it is drawn at 138/154px.
type PlatePosition = { left: Responsive; top: Responsive; size: Responsive }

const makePlate = ({ left, top, size }: PlatePosition) =>
  withCitrusTheme('img', (theme) => [
    { position: 'absolute', objectFit: 'contain', pointerEvents: 'none' } as CSSObject,
    theme.responsiveStyle(left, ['left']) as CSSObject,
    theme.responsiveStyle(top, ['top']) as CSSObject,
    theme.responsiveStyle(size, ['width']) as CSSObject,
    theme.responsiveStyle(size, ['height']) as CSSObject,
  ])

// Same frame as the donburi, so the two padded dishes render at the same size. Grown up and to
// the right from the Figma position, so its lower-left edge keeps its distance from the heading.
const SalmonPlate = makePlate({
  left: ['calc(50% + 98px)', null, 'calc(50% + 99px)', 'calc(50% + 94px)'],
  top: ['-79px', null, '-93px', '-76px'],
  size: ['180px', null, '180px', '200px'],
})
const DonburiPlate = makePlate({
  left: ['calc(50% - 251px)', null, 'calc(50% - 299px)', 'calc(50% - 316px)'],
  top: ['138px', null, '183px', '189px'],
  size: ['180px', null, '180px', '200px'],
})
// The curry tucks under the card's top-right corner, so it follows the card rather than the
// heading. The homepage banner reused here is 31px taller than the frames' banner from tablet up,
// which pushes the card down by the same amount, so the frame tops are shifted 31px to keep the
// overlap. At 640 there is no frame, and the mobile offset would hide the plate behind the wider
// card, so it keeps the mobile frame's overhang past the card's right edge instead.
const CurryPlate = makePlate({
  left: ['calc(50% + 93px)', 'calc(50% + 224px)', 'calc(50% + 141px)', 'calc(50% + 295px)'],
  top: ['278px', null, '279px', '273px'],
  size: ['138px', null, '138px', '154px'],
})

// The browser's display widths for each plate, so it fetches the 360w variant where that will do.
const FULL_PLATE_SIZES = '(min-width: 1024px) 200px, 180px'
const INSET_PLATE_SIZES = '(min-width: 1024px) 154px, 138px'

// Citrus has no text-shadow prop. The shadow keeps the white heading legible where a plate
// passes behind it.
const Heading = withCitrusTheme(Heading1, () => ({
  textShadow: '0 0 10px rgba(33, 41, 48, 0.6)',
}))

// The frames break the heading differently: "How much is / a Gousto box?" on mobile, and
// "How much is a / Gousto box?" from tablet up, where the 345px column wraps it naturally.
const BreakMobile = withCitrusTheme('br', (theme) =>
  theme.responsiveStyle(['inline', null, 'none'], ['display']),
)

// Box has paddingBottom but no paddingTop, and the frames differ only at the top (56px, 80px on
// desktop). Positioned after the plates so the heading, banner and card paint over them.
const ContentColumn = withCitrusTheme(Box, (theme) => [
  { position: 'relative' } as CSSObject,
  theme.responsiveStyle(['56px', null, null, '80px'], ['paddingTop']) as CSSObject,
])

type PlateProps = {
  Plate: ReturnType<typeof makePlate>
  png: string
  pngSm: string
  webp: string
  webpSm: string
  sizes: string
}

const DecorativePlate = ({ Plate, png, pngSm, webp, webpSm, sizes }: PlateProps) => (
  <picture style={{ display: 'contents' }}>
    <source type="image/webp" srcSet={`${webpSm} 360w, ${webp} 500w`} sizes={sizes} />
    <Plate
      src={png}
      srcSet={`${pngSm} 360w, ${png} 500w`}
      sizes={sizes}
      alt=""
      data-testid="box-prices-plate"
    />
  </picture>
)

export function BoxPriceCalculatorHero({
  onBannerCTAClick,
  children,
}: React.PropsWithChildren<BoxPriceCalculatorHeroProps>) {
  return (
    <Box
      data-testid="box-price-calculator-hero"
      position={DisplayPosition.Relative}
      overflowX={Overflow.Hidden}
      overflowY={Overflow.Hidden}
      width="100%"
      bg={Color.ColdGrey_800}
    >
      <DecorativePlate
        Plate={SalmonPlate}
        png={salmon}
        pngSm={salmonSm}
        webp={salmonWebp}
        webpSm={salmonWebpSm}
        sizes={FULL_PLATE_SIZES}
      />
      <DecorativePlate
        Plate={DonburiPlate}
        png={kingPrawnTeriyakiDonburi}
        pngSm={kingPrawnTeriyakiDonburiSm}
        webp={kingPrawnTeriyakiDonburiWebp}
        webpSm={kingPrawnTeriyakiDonburiWebpSm}
        sizes={FULL_PLATE_SIZES}
      />
      <DecorativePlate
        Plate={CurryPlate}
        png={potatoCurry}
        pngSm={potatoCurrySm}
        webp={potatoCurryWebp}
        webpSm={potatoCurryWebpSm}
        sizes={INSET_PLATE_SIZES}
      />
      <ContentColumn
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        alignItems={AlignItems.Center}
        gap={6}
        paddingBottom={14}
        paddingH={[4, null, 4, 6]}
      >
        <Box maxWidth="345px">
          <Heading
            color={Color.White}
            fontFamily={FontFamily.ExtraBold}
            size={[14, null, 13]}
            textAlign={TextAlign.Center}
          >
            How much is
            <BreakMobile />
            {' a Gousto box?'}
          </Heading>
        </Box>
        {/* No ** marker: /box-prices doesn't render the landing-page footer note it points to. */}
        <DiscountBanner
          onBannerCTAClick={onBannerCTAClick}
          trackingSection={Sections.BOX_PRICES}
          showTermsMarker={false}
        />
        {children}
      </ContentColumn>
    </Box>
  )
}
